import { ctaDesc, ctaTitle } from "@/lib/seo";
import { RelatedHubGrid } from "@/components/RelatedHubGrid";
import QuotableStatsStrip from "@/components/QuotableStatsStrip";
import { createFileRoute, Link } from "@tanstack/react-router";
import { THAI_EMBASSIES_ABROAD } from "@/data/thai-embassies-abroad";

const SITE = "https://nyclanguageinstitute.com";
const URL = `${SITE}/en/thai-embassy`;
const REGIONS: { key: string; label: string }[] = [
  { key: "americas", label: "Americas" },
  { key: "europe", label: "Europe" },
  { key: "asia", label: "Asia" },
  { key: "oceania", label: "Oceania" },
  { key: "middle-east", label: "Middle East" },
  { key: "africa", label: "Africa" },
];

export const Route = createFileRoute("/en/thai-embassy/")({
  head: () => ({
    meta: [
      { title: ctaTitle("Royal Thai Embassies & Consulates — Directory | NYC", "/en/thai-embassy", "en")},
      { name: "description", content: ctaDesc("Directory of Royal Thai embassies and consulates-general abroad — addresses, phones, hours. Talk to a specialist today; no obligation, fast reply.", "/en/thai-embassy", "en")},
      { property: "og:title", content: ctaTitle("Royal Thai Embassies & Consulates Worldwide", "/en/thai-embassy", "en")},
      { property: "og:description", content: ctaDesc("Directory of Royal Thai embassies and consulates-general abroad — addresses, phones, hours, consular jurisdictions, and legalization fees.", "/en/thai-embassy", "en")},
      { property: "og:url", content: URL },
      { property: "og:locale", content: "en_US" },
      { property: "og:locale:alternate", content: "th_TH" },
    ],
    links: [
      { rel: "canonical", href: URL },
      { rel: "alternate", hreflang: "th", href: `${SITE}/thai-embassy` },
      { rel: "alternate", hreflang: "en", href: URL },
      { rel: "alternate", hreflang: "x-default", href: `${SITE}/thai-embassy` },
    ],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify({
        "@context": "https://schema.org", "@type": "CollectionPage",
        "@id": `${URL}#collection`, url: URL,
        name: "Royal Thai Embassies & Consulates Worldwide",
        inLanguage: "en", numberOfItems: THAI_EMBASSIES_ABROAD.length,
      }) },
    ],
  }),
  component: EnThaiEmbassyHub,
});

function EnThaiEmbassyHub() {
  return (
    <main className="min-h-screen bg-white" lang="en">
      <section className="bg-navy-deep px-4 py-14 text-white sm:py-20">
        <div className="mx-auto max-w-5xl">
          <p className="text-xs font-bold uppercase tracking-widest text-amber-300">Royal Thai Diplomatic Missions</p>
          <h1 className="mt-3 font-display text-3xl font-extrabold sm:text-5xl">Thai Embassies & Consulates Worldwide</h1>
          <p className="mt-4 max-w-3xl text-white/85">Directory of {THAI_EMBASSIES_ABROAD.length}+ Royal Thai embassies and consulates-general — passport renewal, notarization, legalization, marriage & civil registration for Thai citizens abroad.</p>
        </div>
      </section>
      <div className="mx-auto max-w-6xl px-4 py-12">
        {REGIONS.map((r) => {
          const items = THAI_EMBASSIES_ABROAD.filter((e) => e.region === r.key);
          if (!items.length) return null;
          return (
            <section key={r.key} className="mt-10 first:mt-0">
              <h2 className="font-display text-2xl font-extrabold text-navy-deep">{r.label} <span className="text-slate-400 text-base">({items.length})</span></h2>
              <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {items.map((e) => (
                  <Link key={e.slug} to="/en/thai-embassy/$country" params={{ country: e.slug }}
                    className="group flex items-start gap-3 rounded-2xl border border-slate-200 bg-white p-4 hover:border-amber-400">
                    <div className="text-2xl">{e.flag}</div>
                    <div>
                      <div className="font-display text-sm font-extrabold text-navy-deep group-hover:text-amber-600">Royal Thai — {e.city}</div>
                      <div className="mt-1 text-[11px] text-slate-500 line-clamp-2">{e.jurisdiction}</div>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          );
        })}
      </div>
          <QuotableStatsStrip cluster="embassy" locale="en" variant="light" />
    <RelatedHubGrid locale="en" /></main>
  );
}
